import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";

import { QueryTypes } from "@medplum/core";
import { Resource, ResourceType } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { RootState, useAppDispatch } from "@/store";
import {
  ResourcePollingData,
  selectPollingDataByKey,
  stopResourcePolling,
} from "@/store/slices/resource-polling";

import { getResourcesFromBundle } from "../medplum";

const DEFAULT_INTERVAL = 1000;
const DEFAULT_MAX_EXECUTIONS = 4;

export default function useResourcePolling(
  key: string,
  resourceType: ResourceType,
  query: QueryTypes,
  callback: (resource: Resource[]) => Promise<void>,
  onError: (error: Error) => void,
  options: { interval?: number; maxExecutions?: number } = {},
) {
  const medplum = useMedplum();
  const timeout = useRef<NodeJS.Timeout>();
  const executionCount = useRef<number>(0);

  const dispatch = useAppDispatch();
  const { isActive, lastCalled } = useSelector<RootState, ResourcePollingData>((state) =>
    selectPollingDataByKey(state, key),
  );

  const maxExecutions = useMemo(() => options.maxExecutions ?? DEFAULT_MAX_EXECUTIONS, [options]);
  const interval = useMemo(() => options.interval ?? DEFAULT_INTERVAL, [options]);

  const stopPolling = useCallback(() => {
    executionCount.current = 0;
    dispatch(stopResourcePolling(key));
    timeout.current && clearInterval(timeout.current);
  }, [dispatch, key]);

  useEffect(() => {
    executionCount.current = 0;
  }, [lastCalled]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  useEffect(() => {
    const poll = async () => {
      if (!isActive) {
        stopPolling();
        return;
      }

      try {
        executionCount.current += 1;
        if (maxExecutions && executionCount.current > maxExecutions) {
          stopPolling();
          return;
        }

        medplum.invalidateSearches(resourceType);
        const { bundle } = await medplum.searchResources(resourceType, query);
        const resources = getResourcesFromBundle(resourceType, bundle);
        await callback(resources);
      } catch (error) {
        stopPolling();
        onError(error as Error);
      }
    };

    if (!isActive) {
      executionCount.current = 0;
      return;
    }

    if (executionCount.current === 0) {
      // Only called for the first time after polling is started
      poll();
      timeout.current = setInterval(poll, interval);
    }
  }, [
    isActive,
    interval,
    stopPolling,
    callback,
    maxExecutions,
    medplum,
    onError,
    query,
    resourceType,
  ]);
}
